import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "../ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import { getPageRange } from "../ui/pagination-utils";
import { cn } from "../ui/utils";

/** 跳转输入最大位数的硬上限，兜底防止 totalPages 异常超大时出现长条输入框 */
const JUMP_INPUT_HARD_MAX_DIGITS = 9;

/**
 * 过滤非数字字符，并按 `totalPages` 的位数与数值进行实时夹限。
 *
 * - 非数字字符被移除
 * - 超出 `totalPages` 位数的部分被截断（根本敲不进超限值的「前几位」）
 * - 截断后的数值仍 > `totalPages` 时，直接 clamp 到 `String(totalPages)`
 * - 值为 `0` 时清空（保留空串，由外层判定为「无效跳转」）
 */
function clampJumpInput(raw: string, totalPages: number): string {
  const safeTotalPages = Math.max(1, Math.floor(totalPages));
  const maxDigits = Math.min(
    JUMP_INPUT_HARD_MAX_DIGITS,
    String(safeTotalPages).length,
  );

  const digits = raw.replace(/\D/g, "").slice(0, maxDigits);
  if (digits === "") return "";

  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n > safeTotalPages) return String(safeTotalPages);
  return String(n);
}

export type TablePaginationFooterProps = {
  page: number;
  totalPages: number;
  /** 总条数（精确或「至少」下限，由 totalItemsMode 决定） */
  totalItems: number;
  /** `exact`：共 N 条；`minimum`：不少于 N 条（接口未返回 total 且当前页满页时） */
  totalItemsMode?: "exact" | "minimum";
  onPageChange: (page: number) => void;
  className?: string;
};

export function TablePaginationFooter({
  page,
  totalPages,
  totalItems,
  totalItemsMode = "exact",
  onPageChange,
  className,
}: TablePaginationFooterProps) {
  const { t } = useTranslation();
  const [jumpValue, setJumpValue] = useState("");

  /** 输入框当前允许的最大位数，随 totalPages 动态变化，避免用户能先敲超限前缀再被 clamp 造成跳变 */
  const jumpMaxDigits = useMemo(() => {
    const safeTotalPages = Math.max(1, Math.floor(totalPages));
    return Math.min(
      JUMP_INPUT_HARD_MAX_DIGITS,
      String(safeTotalPages).length,
    );
  }, [totalPages]);

  /**
   * 将当前输入解析为可跳转页：
   * - 空串：`null`（表示无效/无变化）
   * - 非法 / 越界 / 等于当前页：`null`
   * - 合法且不同于当前页：返回目标页号
   */
  const pendingJumpTarget = useMemo<number | null>(() => {
    if (jumpValue === "") return null;
    const n = parseInt(jumpValue, 10);
    if (!Number.isFinite(n)) return null;
    if (n < 1 || n > totalPages) return null;
    if (n === page) return null;
    return n;
  }, [jumpValue, page, totalPages]);

  const canJump = pendingJumpTarget !== null;

  const commitJump = useCallback(() => {
    if (pendingJumpTarget === null) {
      if (jumpValue !== "") setJumpValue("");
      return;
    }
    setJumpValue("");
    onPageChange(pendingJumpTarget);
  }, [jumpValue, onPageChange, pendingJumpTarget]);

  if (totalPages <= 1) return null;

  const totalText =
    totalItemsMode === "minimum"
      ? t("common.pagination.totalMinimum", {
          total: totalItems.toLocaleString(),
        })
      : t("common.paginationTotal", { total: totalItems.toLocaleString() });

  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 mt-2 border-t border-slate-200 dark:border-[#D2F0E1]/5",
        className,
      )}
    >
      <p className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
        {totalText}
      </p>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end sm:justify-end min-w-0">
        <Pagination className="mx-0 w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                disabled={page <= 1}
                onClick={() => {
                  if (page > 1) onPageChange(page - 1);
                }}
              />
            </PaginationItem>
            {getPageRange(page, totalPages).map((p, idx) =>
              p === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${idx}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={`page-${p}`}>
                  <PaginationLink
                    isActive={p === page}
                    onClick={() => {
                      if (p !== page) onPageChange(p);
                    }}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                disabled={page >= totalPages}
                onClick={() => {
                  if (page < totalPages) onPageChange(page + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        <span
          className="hidden sm:block h-4 w-px shrink-0 bg-slate-200 dark:bg-slate-700"
          aria-hidden
        />
        <label className="flex items-center gap-1.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400">
          <span className="whitespace-nowrap shrink-0">
            {t("common.pagination.jumpLabel")}
          </span>
          <Input
            type="text"
            inputMode="numeric"
            value={jumpValue}
            maxLength={jumpMaxDigits}
            onChange={(e) =>
              setJumpValue(clampJumpInput(e.target.value, totalPages))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (canJump) commitJump();
              }
            }}
            onBlur={() => {
              if (jumpValue !== "" && !canJump) setJumpValue("");
            }}
            placeholder={t("common.pagination.jumpPlaceholder")}
            className="h-8 min-w-[3.25rem] w-16 sm:w-20 px-2 text-center text-xs tabular-nums"
            aria-label={t("common.pagination.jumpAria")}
          />
          <button
            type="button"
            onClick={commitJump}
            disabled={!canJump}
            className="h-8 shrink-0 rounded-md border border-slate-200 bg-white px-2 sm:px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:border-[#D2F0E1]/20 dark:bg-[#0E0F11] dark:text-slate-200 dark:hover:bg-[#141517] dark:disabled:hover:bg-[#0E0F11]"
          >
            {t("common.pagination.go")}
          </button>
        </label>
      </div>
    </div>
  );
}
